import { useCallback } from 'react';
import { type Location, type To, useLocation, useNavigate } from 'react-router';

const GO_BACK = 'GO_BACK';

export interface BackToDestination {
    /** The route for the return destination */
    route?: To;
    /** The display name for the return destination */
    text: string;
}

/**
 * Given a default return destination, provide a function to navigate to the previous page.
 * If the current page is the first in the history stack, use the default destination from
 * `backToDestinationDefault`.
 */
export const useGoBack = (backToDestinationDefault: {
    /** The route for the default return destination */
    route: To;
    /** The display name for the default return destination */
    text: string;
}): {
    backToDestination: BackToDestination;
    goBack: (replace?: boolean) => void;
} => {
    const location = useLocation() as Location<{ backToDestination: BackToDestination }>;
    const navigate = useNavigate();

    // If current location was navigated to internally:
    // 1. If the location state has a 'backToDestination.route', use it.
    // 2. Else, use delta of -1 to go to previous location record.
    // Else, use default route.
    let backToDestination;
    if (location.state?.backToDestination) {
        backToDestination = {
            route: GO_BACK,
            ...location.state?.backToDestination,
        };
    } else {
        backToDestination = backToDestinationDefault;
    }
    const goBack = useCallback<(replace?: boolean) => void>(
        (replace = false) => {
            if (backToDestination.route === GO_BACK) {
                navigate(-1);
                return;
            }
            navigate(backToDestination.route, {
                replace,
            });
        },
        [backToDestination, navigate],
    );

    return { backToDestination, goBack };
};
